import {useMemo} from "react";
import SentryEchart from "./sentry-echart.jsx";
import useGraphViewportWidth from "../../hooks/use-graph-viewport-width.js";
import {DASH_BREAKPOINTS, fluidPx} from "../../utils/responsive-graph.js";

const START_ANGLE = 210;
const END_ANGLE = -30;
const CENTER = ["50%", "52%"];
const OUTER_RADIUS = "95%";
const INNER_RADIUS = "70%";

const HIDDEN_AXIS = {show: false};

export default function DoubleArcGauge({
    value,
    max,
    label,
    className = "",
    height = "100%",
    valueFontSize,
    labelFontSize,
    labelOffsetCenter,
}) {
    const vw = useGraphViewportWidth();
    const numericValue = Number(value) || 0;
    const numericMax = Number(max) || 0;

    const option = useMemo(() => {
        const normalizedMax = Math.max(numericMax, 0);
        const normalizedValue = Math.min(Math.max(numericValue, 0), normalizedMax);
        const chartMax = normalizedMax || 1;
        const isCompact = vw < DASH_BREAKPOINTS.desktop;
        const outerWidth = fluidPx(8, 16, vw);
        const innerWidth = fluidPx(5, 10, vw);
        const resolvedValueFontSize = valueFontSize ?? fluidPx(20, 32, vw);
        const resolvedLabelFontSize = labelFontSize ?? fluidPx(10, 12, vw);
        const resolvedLabelOffset = labelOffsetCenter ?? (isCompact ? "72%" : "76%");

        return {
            animation: true,
            tooltip: {show: false},
            series: [
                {
                    name: label,
                    type: "gauge",
                    startAngle: START_ANGLE,
                    endAngle: END_ANGLE,
                    min: 0,
                    max: chartMax,
                    center: CENTER,
                    radius: OUTER_RADIUS,
                    z: 2,
                    silent: true,
                    pointer: HIDDEN_AXIS,
                    axisTick: HIDDEN_AXIS,
                    splitLine: HIDDEN_AXIS,
                    axisLabel: HIDDEN_AXIS,
                    progress: {
                        show: true,
                        roundCap: true,
                        width: outerWidth,
                        itemStyle: {
                            color: {
                                type: "linear",
                                x: 1,
                                y: 0,
                                x2: 0,
                                y2: 1,
                                colorStops: [
                                    {offset: 0, color: "#49EEEB"},
                                    {offset: 0.12, color: "#1CEAE6"},
                                    {offset: 0.25, color: "#3E98D9"},
                                    {offset: 0.5, color: "#0080ED"},
                                    {offset: 1, color: "#003878"},
                                ],
                            },
                        },
                    },
                    axisLine: {
                        show: true,
                        roundCap: true,
                        lineStyle: {
                            width: outerWidth,
                            color: [[1, "rgba(175, 183, 199, 0.28)"]],
                        },
                    },
                    detail: {
                        show: true,
                        formatter: "{value}",
                        color: "#F4FFFF",
                        fontSize: resolvedValueFontSize,
                        fontWeight: 700,
                        offsetCenter: [0, isCompact ? "2%" : 0],
                    },
                    title: {
                        show: true,
                        offsetCenter: [0, resolvedLabelOffset],
                        color: "#AFAFAF",
                        fontSize: resolvedLabelFontSize,
                    },
                    data: [{value: normalizedValue, name: String(label ?? "")}],
                },
                {
                    type: "gauge",
                    startAngle: START_ANGLE,
                    endAngle: END_ANGLE,
                    min: 0,
                    max: 100,
                    center: CENTER,
                    radius: INNER_RADIUS,
                    z: 1,
                    silent: true,
                    pointer: HIDDEN_AXIS,
                    axisTick: HIDDEN_AXIS,
                    splitLine: HIDDEN_AXIS,
                    axisLabel: HIDDEN_AXIS,
                    progress: {
                        show: true,
                        roundCap: true,
                        width: innerWidth,
                        itemStyle: {
                            color: {
                                type: "linear",
                                x: 0,
                                y: 0,
                                x2: 0,
                                y2: 1,
                                colorStops: [
                                    {offset: 0, color: "#087BD8"},
                                    {offset: 1, color: "#003878"},
                                ],
                            },
                        },
                    },
                    axisLine: {
                        show: true,
                        lineStyle: {
                            width: innerWidth,
                            color: [[1, "transparent"]],
                        },
                    },
                    detail: HIDDEN_AXIS,
                    title: HIDDEN_AXIS,
                    data: [{value: 100}],
                },
            ],
        };
    }, [label, labelFontSize, labelOffsetCenter, numericMax, numericValue, valueFontSize, vw]);

    return (
        <div className={["h-full min-h-0", className].filter(Boolean).join(" ")}>
            <SentryEchart option={option} height={height}/>
        </div>
    );
}
